import * as Sentry from "@sentry/react";
import { post } from "../http/http.service";
import { CreateSearchIntentResponse } from "./createSearchIntentResponse";
import { FlightDataRequestError } from "./flight-data-request-error";

type FlightSearchPayload = {
  requestId?: string;
  searchIntent?: Record<string, unknown>;
};

const getErrorStatus = (error: unknown) => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "status" in error.response
  ) {
    return error.response.status;
  }

  return undefined;
};

const MAX_RESPONSE_BODY_LENGTH = 500;

const getErrorCode = (error: unknown): string | undefined => {
  const code = (error as { code?: unknown } | null)?.code;

  return typeof code === "string" ? code : undefined;
};

/** Corpo da resposta de erro da API, em texto e truncado. */
const getErrorResponseBody = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: unknown } } | null)?.response
    ?.data;

  if (data === undefined || data === null || data === "") {
    return undefined;
  }

  const text = typeof data === "string" ? data : JSON.stringify(data);

  return text.slice(0, MAX_RESPONSE_BODY_LENGTH);
};

/**
 * Faixa da duração até a falha. Separa erro de rede rápido (bloqueio local,
 * conexão) de lentidão do servidor (o timeout do cliente é de 10s).
 */
const getDurationBand = (durationMs: number) => {
  if (durationMs < 1000) return "lt-1s";
  if (durationMs < 5000) return "1-5s";
  if (durationMs < 10000) return "5-10s";

  return "gte-10s";
};

/**
 * O Sentry normaliza `contexts` em 3 níveis e troca os arrays do 4º por
 * "[Array]", o que escondia as datas da busca. As listas viram texto separado
 * por vírgula.
 */
const flattenSearchIntent = (searchIntent?: Record<string, unknown>) =>
  searchIntent &&
  Object.fromEntries(
    Object.entries(searchIntent).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(",") : value,
    ]),
  );

const getFlightSearchContext = (payload: object) => {
  const flightPayload = payload as FlightSearchPayload;

  return {
    requestId: flightPayload.requestId,
    searchIntent: flattenSearchIntent(flightPayload.searchIntent),
  };
};

/**
 * Faz a requisição de dados de voos.
 * @param url - URL da API de busca de voos.
 * @param payload - Dados para realizar a busca de voos.
 * @returns Dados retornados pela API de busca de voos.
 */
export const fetchFlightData = async (
  url: string,
  payload: object,
): Promise<CreateSearchIntentResponse> => {
  const startedAt = Date.now();
  const flightSearchContext = getFlightSearchContext(payload);

  Sentry.addBreadcrumb({
    category: "flight-search",
    message: "createSearchIntent request started",
    level: "info",
    data: {
      url,
      ...flightSearchContext,
    },
  });

  try {
    const response = await Sentry.startSpan(
      {
        op: "http.client",
        name: "POST /createSearchIntent",
        attributes: {
          "http.request.method": "POST",
          "url.full": url,
          "flight_search.request_id": flightSearchContext.requestId ?? "",
        },
      },
      () =>
        post(url, payload, {
          "Content-Type": "application/json",
          Authorization: "$3394@gjfy^W$W2Z@Rmf!2!s@aA&%*6h",
        }),
    );

    Sentry.addBreadcrumb({
      category: "flight-search",
      message: "createSearchIntent response received",
      level: "info",
      data: {
        url,
        status: response.status,
        durationMs: Date.now() - startedAt,
        requestId: flightSearchContext.requestId,
      },
    });

    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    const durationMs = Date.now() - startedAt;
    const errorCode = getErrorCode(error);

    Sentry.captureException(error, {
      tags: {
        feature: "flight-search",
        endpoint: "createSearchIntent",
        status: status ? String(status) : "unknown",
        errorCode: errorCode ?? "unknown",
        durationBand: getDurationBand(durationMs),
      },
      contexts: {
        flightSearch: {
          url,
          status,
          code: errorCode,
          responseBody: getErrorResponseBody(error),
          durationMs,
          ...flightSearchContext,
        },
      },
    });

    console.error("Erro ao buscar dados de voos:", error);
    throw new FlightDataRequestError(error);
  }
};
